import type { FC } from "react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useRefresh } from "../../contexts/RefreshContext";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import {
  getWorkerById,
  getWorkerIdForUser,
  getAggregatedWorkerGoals,
} from "../../services/workers";
import {
  getAccumulatedRevenue,
  getIndividualRevenue,
} from "../../services/revenue";
import {
  getMultiTeamRevenue,
  getWorkerCombinedTeamMembers,
} from "../../services/teams";
import { HiArrowUp, HiArrowDown } from "react-icons/hi";
import { clearContractMetadataCache } from "../../utils/contractDataCache";

interface WorkerGoals {
  daily: number;
  monthly: number;
}

interface HistoricalData {
  daily: {
    current: number;
    previous: number;
    sparkline: number[];
  };
  weekly: {
    current: number;
    previous: number;
    sparkline: number[];
  };
  monthly: {
    current: number;
    previous: number;
    sparkline: number[];
  };
}

interface RevenueCardsProps {
  viewMode: "all" | "individual";
  isAdminRole: boolean;
  roleId: number | null;
}

const RevenueCards: FC<RevenueCardsProps> = function ({
  viewMode,
  isAdminRole,
  roleId,
}) {
  const { triggerRefresh } = useRefresh();
  const [dailyRevenue, setDailyRevenue] = useState<number | null>(null);
  const [weeklyRevenue, setWeeklyRevenue] = useState<number | null>(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState<number | null>(null);
  const [monthlyForecast, setMonthlyForecast] = useState<number | null>(null);
  const [workerGoals, setWorkerGoals] = useState<WorkerGoals>({
    daily: 0,
    monthly: 0,
  });
  const [historicalData, setHistoricalData] = useState<HistoricalData>({
    daily: { current: 0, previous: 0, sparkline: [0, 0, 0, 0, 0, 0, 0] },
    weekly: { current: 0, previous: 0, sparkline: [0, 0, 0, 0] },
    monthly: { current: 0, previous: 0, sparkline: [0, 0, 0] },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchRevenue = useCallback(async () => {
    try {
      setError(null);
      if (!user) {
        setLoading(false);
        return;
      }

      // Get worker data
      const currentWorkerId = await getWorkerIdForUser(user.id);
      const workerData = await getWorkerById(currentWorkerId);

      if (!workerData) {
        setError("No worker found for user");
        return;
      }

      let teamWorkerIds: number[] | undefined;
      if (isAdminRole && viewMode === "all" && roleId === 3) {
        const teamMembers = await getWorkerCombinedTeamMembers(currentWorkerId);
        teamWorkerIds = [
          currentWorkerId,
          ...teamMembers.map((member) => member.id),
        ];
      }

      // Set goals based on view mode and role
      if (isAdminRole && viewMode === "all") {
        const aggregatedGoals = await getAggregatedWorkerGoals(teamWorkerIds);
        setWorkerGoals({
          daily: aggregatedGoals.daily,
          monthly: aggregatedGoals.monthly,
        });
      } else {
        // For individual view or non-admin, use individual worker goals
        setWorkerGoals({
          daily: workerData.revenue_goal_daily || 0,
          monthly: workerData.revenue_goal_month || 0,
        });
      }

      // Fetch revenue based on role and view mode
      let revenueData;
      if (isAdminRole && viewMode === "all") {
        if (roleId === 3) {
          // Team Lead viewing all logic (aggregated team data)
          revenueData = await getMultiTeamRevenue(currentWorkerId);
        } else {
          // Admin viewing all logic (global data)
          revenueData = await getAccumulatedRevenue();
        }
      } else {
        // Individual logic
        revenueData = await getIndividualRevenue(currentWorkerId);
      }

      setDailyRevenue(revenueData.daily);
      setWeeklyRevenue(revenueData.weekly);
      setMonthlyRevenue(revenueData.monthly);
      setMonthlyForecast(revenueData.forecast);

      // Debug revenue data for troubleshooting
      console.log("Revenue data debug:", {
        viewMode,
        isAdminRole,
        monthlyRevenue: revenueData.monthly,
        monthlyForecast: revenueData.forecast,
        workerGoals: workerGoals,
      });

      // Fetch historical data for trends and sparklines
      await fetchHistoricalData(currentWorkerId);

      setError(null);
    } catch (err) {
      console.error("Error fetching revenue:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch revenue data"
      );
    } finally {
      setLoading(false);
    }
  }, [user, viewMode, isAdminRole, roleId]);

  const fetchHistoricalData = async (workerId: number) => {
    try {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // Format date to YYYY-MM-DD
      const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Daily trend - today vs yesterday
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      // Last 7 days for daily sparkline
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        last7Days.push(formatDate(date));
      }

      // Weekly trend - this week vs last week (Monday as start of week)
      const thisWeekStart = new Date(today);
      const dayOfWeek = today.getDay();
      const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so Sunday needs 6 days back to Monday
      thisWeekStart.setDate(today.getDate() - daysFromMonday);

      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(thisWeekStart.getDate() - 7);

      const lastWeekEnd = new Date(thisWeekStart);
      lastWeekEnd.setDate(thisWeekStart.getDate() - 1);

      // Prepare queries based on role and viewMode
      let queryFilter: any = {};

      if (isAdminRole && viewMode === "all") {
        if (roleId === 3) {
          // For Team Leads, filter by all their team members
          const teamMembers = await getWorkerCombinedTeamMembers(workerId);
          // Include the worker themselves in the list
          const allWorkerIds = teamMembers.map((m) => m.id);
          if (!allWorkerIds.includes(workerId)) {
            allWorkerIds.push(workerId);
          }
          // Use 'in' filter for multiple worker IDs.
          // Note: Supabase .in() expects an array.
          // We can't use .match() for 'in' logic easily, so we'll construct the query differently below.
          // However, this logic needs to be moved inside the query execution or we change queryFilter to be more flexible.
          // Let's change the query construction below instead of just `queryFilter`.
          queryFilter = { worker_ids: allWorkerIds }; // Custom marker to handle below
        } else {
          // Global admin - no filter
          queryFilter = {};
        }
      } else {
        // Individual view
        queryFilter = { worker_id: workerId };
      }

      // Get the database-formatted date strings for consistent comparison
      const thisWeekStartStr = formatDate(thisWeekStart);
      const lastWeekStartStr = formatDate(lastWeekStart);
      const lastWeekEndStr = formatDate(lastWeekEnd);

      console.log("Week boundaries:", {
        thisWeekStart: thisWeekStartStr,
        lastWeekStart: lastWeekStartStr,
        lastWeekEnd: lastWeekEndStr,
        today: formatDate(today),
      });

      // Helper to apply filters to query
      const applyFilters = (query: any) => {
        if (queryFilter.worker_ids) {
          return query.in("worker_id", queryFilter.worker_ids);
        } else if (queryFilter.worker_id) {
          return query.eq("worker_id", queryFilter.worker_id);
        }
        return query;
      };

      // Get current week data from daily_revenue (Monday to today)
      let currentWeekQuery = supabase
        .from("daily_revenue")
        .select("revenue_date, revenue")
        .gte("revenue_date", thisWeekStartStr)
        .lte("revenue_date", formatDate(today));

      const { data: currentWeekDailyData } = await applyFilters(
        currentWeekQuery
      );

      // Get last week data from daily_revenue (Monday to Sunday of last week)
      let lastWeekQuery = supabase
        .from("daily_revenue")
        .select("revenue_date, revenue")
        .gte("revenue_date", lastWeekStartStr)
        .lte("revenue_date", lastWeekEndStr);

      const { data: lastWeekDailyData } = await applyFilters(lastWeekQuery);

      // Calculate current week sum from daily data
      const currentWeekRevenue = currentWeekDailyData
        ? currentWeekDailyData.reduce(
            (sum: number, entry: { revenue: number | null }) =>
              sum + Number(entry.revenue || 0),
            0
          )
        : 0;

      // Calculate last week sum from daily data for consistency
      const lastWeekRevenue = lastWeekDailyData
        ? lastWeekDailyData.reduce(
            (sum: number, entry: { revenue: number | null }) =>
              sum + Number(entry.revenue || 0),
            0
          )
        : 0;

      // Build weekly sparkline for last 4 weeks using daily data
      const last4WeeksStarts = [];
      for (let i = 3; i >= 0; i--) {
        const weekStart = new Date(thisWeekStart);
        weekStart.setDate(thisWeekStart.getDate() - 7 * i);
        last4WeeksStarts.push(formatDate(weekStart));
      }

      const weeklySparkline: number[] = [];

      for (let i = 0; i < last4WeeksStarts.length; i++) {
        const weekStartStr = last4WeeksStarts[i];
        if (!weekStartStr) continue; // Skip if weekStartStr is undefined

        let weekEndStr: string;

        if (i === last4WeeksStarts.length - 1) {
          // Current week: up to today
          weekEndStr = formatDate(today);
        } else {
          // Other weeks: full week (6 days after start = Sunday)
          const weekStartDate = new Date(weekStartStr);
          const weekEndDate = new Date(
            weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000
          );
          weekEndStr = formatDate(weekEndDate);
        }

        let weekQuery = supabase
          .from("daily_revenue")
          .select("revenue_date, revenue")
          .gte("revenue_date", weekStartStr)
          .lte("revenue_date", weekEndStr);

        const { data: weekData } = await applyFilters(weekQuery);

        const weekRevenue = weekData
          ? weekData.reduce(
              (sum: number, entry: { revenue: number | null }) =>
                sum + Number(entry.revenue || 0),
              0
            )
          : 0;

        weeklySparkline.push(weekRevenue);
      }

      // Debug weekly data
      console.log("Weekly revenue breakdown:", {
        currentWeekRevenue,
        lastWeekRevenue,
        weeklySparkline,
        currentWeekData: currentWeekDailyData,
        lastWeekData: lastWeekDailyData,
        percentageChange: calculateChange(currentWeekRevenue, lastWeekRevenue),
      });

      // Monthly trend - this month vs last month
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastMonthStart = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1
      );
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

      // Last 3 months for monthly sparkline
      const last3Months = [];
      for (let i = 2; i >= 0; i--) {
        const monthStart = new Date(
          today.getFullYear(),
          today.getMonth() - i,
          1
        );
        last3Months.push(formatDate(monthStart));
      }

      // Daily historical data
      let dailyQuery = supabase
        .from("daily_revenue")
        .select("revenue_date, revenue")
        .in("revenue_date", [
          formatDate(today),
          formatDate(yesterday),
          ...last7Days,
        ]);

      const { data: dailyData } = await applyFilters(dailyQuery);

      // Monthly historical data
      let monthlyQuery = supabase
        .from("daily_revenue")
        .select("revenue_date, revenue")
        .gte("revenue_date", formatDate(lastMonthStart))
        .lte("revenue_date", formatDate(today));

      const { data: monthlyData } = await applyFilters(monthlyQuery);

      // Process daily data
      const todayRevenue =
        isAdminRole && viewMode === "all"
          ? dailyData
              ?.filter(
                (d: { revenue_date: string }) =>
                  d.revenue_date === formatDate(today)
              )
              .reduce(
                (sum: number, entry: { revenue: number | null }) =>
                  sum + Number(entry.revenue || 0),
                0
              ) || 0
          : dailyData?.find(
              (d: { revenue_date: string }) =>
                d.revenue_date === formatDate(today)
            )?.revenue || 0;

      const yesterdayRevenue =
        isAdminRole && viewMode === "all"
          ? dailyData
              ?.filter(
                (d: { revenue_date: string }) =>
                  d.revenue_date === formatDate(yesterday)
              )
              .reduce(
                (sum: number, entry: { revenue: number | null }) =>
                  sum + Number(entry.revenue || 0),
                0
              ) || 0
          : dailyData?.find(
              (d: { revenue_date: string }) =>
                d.revenue_date === formatDate(yesterday)
            )?.revenue || 0;

      // Daily sparkline - get values for last 7 days
      const dailySparkline = last7Days.map((date) => {
        if (isAdminRole && viewMode === "all") {
          return (
            dailyData
              ?.filter((d: { revenue_date: string }) => d.revenue_date === date)
              .reduce(
                (sum: number, entry: { revenue: number | null }) =>
                  sum + Number(entry.revenue || 0),
                0
              ) || 0
          );
        } else {
          const entry = dailyData?.find(
            (d: { revenue_date: string }) => d.revenue_date === date
          );
          return entry ? Number(entry.revenue || 0) : 0;
        }
      });

      // Process monthly data
      const thisMonthRevenue = monthlyData
        ?.filter(
          (d: { revenue_date: string }) =>
            new Date(d.revenue_date) >= thisMonthStart &&
            new Date(d.revenue_date) <= today
        )
        .reduce(
          (sum: number, entry: { revenue: number | null }) =>
            sum + Number(entry.revenue || 0),
          0
        );

      const lastMonthRevenue = monthlyData
        ?.filter(
          (d: { revenue_date: string }) =>
            new Date(d.revenue_date) >= lastMonthStart &&
            new Date(d.revenue_date) <= lastMonthEnd
        )
        .reduce(
          (sum: number, entry: { revenue: number | null }) =>
            sum + Number(entry.revenue || 0),
          0
        );

      // Monthly sparkline - mocked for now, would need actual data
      const monthlySparkline = [
        Number(lastMonthRevenue || 0) * 0.9,
        Number(lastMonthRevenue || 0),
        Number(thisMonthRevenue || 0),
      ];

      setHistoricalData({
        daily: {
          current: Number(todayRevenue),
          previous: Number(yesterdayRevenue),
          sparkline: dailySparkline,
        },
        weekly: {
          current: Number(currentWeekRevenue),
          previous: Number(lastWeekRevenue),
          sparkline: weeklySparkline,
        },
        monthly: {
          current: Number(thisMonthRevenue),
          previous: Number(lastMonthRevenue),
          sparkline: monthlySparkline,
        },
      });
    } catch (error) {
      console.error("Error fetching historical data:", error);
    }
  };

  useEffect(() => {
    let isSubscribed = true;

    const revenueSubscription = supabase
      .channel("revenue_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "daily_revenue",
        },
        () => {
          if (isSubscribed) {
            fetchRevenue().catch(console.error);
          }
        }
      )
      .subscribe();

    if (isSubscribed) {
      fetchRevenue().catch(console.error);
    }

    const refreshInterval = setInterval(() => {
      if (isSubscribed) {
        fetchRevenue().catch(console.error);
      }
    }, 5 * 60 * 1000);

    return () => {
      isSubscribed = false;
      clearInterval(refreshInterval);
      revenueSubscription.unsubscribe().catch(console.error);
    };
  }, [fetchRevenue]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      if (roleId === 1 || roleId === 2) {
        const { error: refreshError } = await supabase.rpc(
          "refresh_revenue_aggregates"
        );
        if (refreshError) throw refreshError;
      }

      clearContractMetadataCache();
      await fetchRevenue();
      triggerRefresh();
    } catch (err) {
      console.error("Error refreshing data:", err);
      setError(err instanceof Error ? err.message : "Failed to refresh data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate percentage change
  const calculateChange = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Format numbers in German style (thousands separator with dot, decimal with comma)
  const formatGermanNumber = (value: number): string => {
    return value.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Format percentage change with sign
  const formatChange = (change: number): string => {
    if (isNaN(change) || !isFinite(change)) {
      return "0.0%";
    }

    return change > 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
  };

  const getSparklineOptions = useCallback(
    (isPositive: boolean): ApexOptions => ({
      chart: {
        type: "line",
        sparkline: {
          enabled: true,
        },
        toolbar: {
          show: false,
        },
        redrawOnParentResize: false,
        redrawOnWindowResize: false,
        parentHeightOffset: 0,
        animations: {
          enabled: true,
          speed: 400,
        },
      },
      stroke: {
        width: 2,
        curve: "smooth" as const,
      },
      colors: [isPositive ? "#10b981" : "#ef4444"],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.5,
          opacityTo: 0,
          stops: [0, 100],
        },
      },
      tooltip: {
        enabled: false,
      },
    }),
    []
  );

  const positiveSparklineOptions = useMemo(
    () => getSparklineOptions(true),
    [getSparklineOptions]
  );
  const negativeSparklineOptions = useMemo(
    () => getSparklineOptions(false),
    [getSparklineOptions]
  );

  return (
    <div className="w-full max-w-full px-0 py-2 sm:px-0 sm:py-2">
      <div className="relative mb-2 space-y-2 md:mb-2 md:space-y-2">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-3">
          {/* Error message */}
          {error && (
            <div className="absolute inset-x-0 top-0 rounded-t-lg bg-red-100 px-2 py-1 text-sm text-red-700 dark:bg-red-900 dark:text-red-100 md:px-3">
              {error}
            </div>
          )}

          {/* Daily Revenue Card */}
          <div className="h-full min-h-[140px] rounded-lg border border-gray-100 bg-white p-2 dark:border-gray-700 dark:bg-gray-800 sm:min-h-[180px] sm:p-3 md:min-h-[200px] md:p-4">
            <div className="flex h-full flex-col justify-between">
              <div className="space-y-1 sm:space-y-2">
                <span className="block overflow-hidden whitespace-normal text-left text-xs text-gray-500 dark:text-gray-400 sm:text-sm md:text-base">
                  Tagesumsatz
                </span>
                <div className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="block text-left text-base font-bold text-gray-900 dark:text-white sm:text-lg md:text-xl lg:text-xl xl:text-2xl">
                      {loading
                        ? "Lädt..."
                        : `${formatGermanNumber(dailyRevenue || 0)}€`}
                    </span>
                    {!loading && historicalData.daily.previous > 0 && (
                      <div className="ml-1 flex items-center whitespace-nowrap sm:ml-2">
                        {calculateChange(
                          historicalData.daily.current,
                          historicalData.daily.previous
                        ) >= 0 ? (
                          <HiArrowUp className="mr-0.5 h-3 w-3 shrink-0 text-green-500 sm:mr-1 sm:h-4 sm:w-4" />
                        ) : (
                          <HiArrowDown className="mr-0.5 h-3 w-3 shrink-0 text-red-500 sm:mr-1 sm:h-4 sm:w-4" />
                        )}
                        <span
                          className={`text-xs font-medium sm:text-sm ${
                            calculateChange(
                              historicalData.daily.current,
                              historicalData.daily.previous
                            ) >= 0
                              ? "text-green-500"
                              : "text-red-500"
                          }`}
                        >
                          {formatChange(
                            calculateChange(
                              historicalData.daily.current,
                              historicalData.daily.previous
                            )
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {!loading && historicalData.daily.previous > 0 && (
                    <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-xs">
                      Gestern:{" "}
                      {formatGermanNumber(historicalData.daily.previous)}€
                    </div>
                  )}
                </div>
              </div>
              {!loading && (
                <div className="mt-1 sm:mt-2">
                  {historicalData.daily.sparkline.some((val) => val > 0) && (
                    <div className="h-8 sm:h-12">
                      <Chart
                        options={
                          calculateChange(
                            historicalData.daily.current,
                            historicalData.daily.previous
                          ) >= 0
                            ? positiveSparklineOptions
                            : negativeSparklineOptions
                        }
                        series={[
                          {
                            name: "Trend",
                            data: historicalData.daily.sparkline.slice(-3),
                          },
                        ]}
                        type="line"
                        height={32}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Weekly Revenue Card */}
          <div className="h-full min-h-[140px] rounded-lg border border-gray-100 bg-white p-2 dark:border-gray-700 dark:bg-gray-800 sm:min-h-[180px] sm:p-3 md:min-h-[200px] md:p-4">
            <div className="flex h-full flex-col justify-between">
              <div className="space-y-1 sm:space-y-2">
                <span className="block overflow-hidden whitespace-normal text-left text-xs text-gray-500 dark:text-gray-400 sm:text-sm md:text-base">
                  Wochenumsatz
                </span>
                <div className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="block text-left text-base font-bold text-gray-900 dark:text-white sm:text-lg md:text-xl lg:text-xl xl:text-2xl">
                      {loading
                        ? "Lädt..."
                        : `${formatGermanNumber(weeklyRevenue || 0)}€`}
                    </span>
                    {!loading &&
                      historicalData.weekly.previous > 0 &&
                      weeklyRevenue !== null && (
                        <div className="ml-1 flex items-center whitespace-nowrap sm:ml-2">
                          {calculateChange(
                            weeklyRevenue,
                            historicalData.weekly.previous
                          ) >= 0 ? (
                            <HiArrowUp className="mr-0.5 h-3 w-3 shrink-0 text-green-500 sm:mr-1 sm:h-4 sm:w-4" />
                          ) : (
                            <HiArrowDown className="mr-0.5 h-3 w-3 shrink-0 text-red-500 sm:mr-1 sm:h-4 sm:w-4" />
                          )}
                          <span
                            className={`text-xs font-medium sm:text-sm ${
                              calculateChange(
                                weeklyRevenue,
                                historicalData.weekly.previous
                              ) >= 0
                                ? "text-green-500"
                                : "text-red-500"
                            }`}
                          >
                            {formatChange(
                              calculateChange(
                                weeklyRevenue,
                                historicalData.weekly.previous
                              )
                            )}
                          </span>
                        </div>
                      )}
                  </div>
                  {!loading && historicalData.weekly.previous > 0 && (
                    <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-xs">
                      Letzte Woche:{" "}
                      {formatGermanNumber(historicalData.weekly.previous)}€
                    </div>
                  )}
                </div>
              </div>
              {!loading &&
                historicalData.weekly.sparkline.some((val) => val > 0) &&
                weeklyRevenue !== null && (
                  <div className="mt-1 sm:mt-2">
                    <div className="h-8 sm:h-12">
                      <Chart
                        options={
                          calculateChange(
                            weeklyRevenue,
                            historicalData.weekly.previous
                          ) >= 0
                            ? positiveSparklineOptions
                            : negativeSparklineOptions
                        }
                        series={[
                          {
                            name: "Trend",
                            data: historicalData.weekly.sparkline,
                          },
                        ]}
                        type="line"
                        height={32}
                      />
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Monthly Revenue Card */}
          <div className="h-full min-h-[140px] rounded-lg border border-gray-100 bg-white p-2 dark:border-gray-700 dark:bg-gray-800 sm:min-h-[180px] sm:p-3 md:min-h-[200px] md:p-4">
            <div className="flex h-full flex-col justify-between">
              <div className="space-y-1 sm:space-y-2">
                <span className="block overflow-hidden whitespace-normal text-left text-xs text-gray-500 dark:text-gray-400 sm:text-sm md:text-base">
                  Monatsumsatz
                </span>
                <div className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="block text-left text-base font-bold text-gray-900 dark:text-white sm:text-lg md:text-xl lg:text-xl xl:text-2xl">
                      {loading
                        ? "Lädt..."
                        : `${formatGermanNumber(monthlyRevenue || 0)}€`}
                    </span>
                    {!loading &&
                      historicalData.monthly.previous > 0 &&
                      monthlyRevenue !== null && (
                        <div className="ml-1 flex items-center whitespace-nowrap sm:ml-2">
                          {calculateChange(
                            monthlyRevenue,
                            historicalData.monthly.previous
                          ) >= 0 ? (
                            <HiArrowUp className="mr-0.5 h-3 w-3 shrink-0 text-green-500 sm:mr-1 sm:h-4 sm:w-4" />
                          ) : (
                            <HiArrowDown className="mr-0.5 h-3 w-3 shrink-0 text-red-500 sm:mr-1 sm:h-4 sm:w-4" />
                          )}
                          <span
                            className={`text-xs font-medium sm:text-sm ${
                              calculateChange(
                                monthlyRevenue,
                                historicalData.monthly.previous
                              ) >= 0
                                ? "text-green-500"
                                : "text-red-500"
                            }`}
                          >
                            {formatChange(
                              calculateChange(
                                monthlyRevenue,
                                historicalData.monthly.previous
                              )
                            )}
                          </span>
                        </div>
                      )}
                  </div>
                  {!loading && historicalData.monthly.previous > 0 && (
                    <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-xs">
                      Letzter Monat:{" "}
                      {formatGermanNumber(historicalData.monthly.previous)}€
                    </div>
                  )}
                </div>
              </div>
              {!loading && (
                <div className="mt-1 sm:mt-2">
                  {historicalData.monthly.sparkline.some((val) => val > 0) &&
                    monthlyRevenue !== null && (
                      <div className="h-8 sm:h-12">
                        <Chart
                          options={
                            calculateChange(
                              monthlyRevenue,
                              historicalData.monthly.previous
                            ) >= 0
                              ? positiveSparklineOptions
                              : negativeSparklineOptions
                          }
                          series={[
                            {
                              name: "Trend",
                              data: historicalData.monthly.sparkline,
                            },
                          ]}
                          type="line"
                          height={32}
                        />
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>

          {/* Monthly Forecast Card */}
          <div className="relative h-full min-h-[140px] rounded-lg border border-gray-100 bg-white p-2 dark:border-gray-700 dark:bg-gray-800 sm:min-h-[180px] sm:p-3 md:min-h-[200px] md:p-4">
            <div className="flex h-full flex-col justify-between">
              <div className="space-y-1 sm:space-y-2">
                <span className="block overflow-hidden whitespace-normal text-left text-xs text-gray-500 dark:text-gray-400 sm:text-sm md:text-base">
                  Monatsprognose
                </span>
                <div className="space-y-0.5">
                  <span className="block text-left text-base font-bold text-gray-900 dark:text-white sm:text-lg md:text-xl lg:text-xl xl:text-2xl">
                    {loading
                      ? "Lädt..."
                      : `${formatGermanNumber(monthlyForecast || 0)}€`}
                  </span>
                  {!loading && workerGoals.monthly > 0 && (
                    <div className="flex items-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {monthlyForecast && workerGoals.monthly
                          ? `${Math.round(
                              (monthlyForecast / workerGoals.monthly) * 100
                            )}% des Ziels`
                          : ""}
                      </span>
                    </div>
                  )}
                </div>
                {!loading && monthlyForecast && monthlyForecast > 0 && (
                  <div className="mt-1 pt-1 text-[10px] text-gray-500 dark:text-gray-400">
                    <div className="flex justify-between text-gray-500 dark:text-gray-400">
                      <span>Aktuell</span>
                      <span>Prognose</span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                      <div
                        className="h-2 rounded-full bg-blue-600"
                        style={{
                          width: `${Math.min(
                            100,
                            ((monthlyRevenue || 0) / monthlyForecast) * 100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="mt-1 flex justify-between text-[10px] text-gray-600 dark:text-gray-300">
                      <span>
                        {formatGermanNumber(monthlyRevenue || 0).replace(
                          ",00",
                          ""
                        )}
                        €
                      </span>
                      <span>
                        {formatGermanNumber(monthlyForecast).replace(",00", "")}
                        €
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 p-1.5 text-gray-500 shadow transition-all hover:bg-gray-200 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-300 dark:focus:ring-gray-600 md:top-4 md:right-4"
              title="Daten aktualisieren"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueCards;
